/**
 * Is a fenced JSON block a RESULT a person should read, or source code? And if it is a result,
 * what shape does it have?
 *
 * ── THE DEFECT THIS EXISTS FOR ───────────────────────────────────────────────────────────────
 *
 * `json` sits in `codePreview.CODE_LANGUAGES` beside `apex`, `typescript` and `sql`, so
 * `blockKind("json")` is `"code"` and a JSON fence renders through exactly the same card as an
 * Apex class: syntax highlighting, a line count, a horizontal scrollbar. That is right for a
 * config file someone is editing and wrong for the thing the agent actually emits, which is a
 * RESULT — `{"satisfied": true, "acceptance_met": [...], "gaps": []}`. A reader of that wants to
 * know whether it was satisfied and what the gaps were; they do not want to parse punctuation.
 *
 * ── WHAT THIS MODULE DECIDES, AND WHAT IT REFUSES TO ─────────────────────────────────────────
 *
 * It answers one question — "can this block be read as a small labelled record?" — and answers
 * `null` whenever it cannot, which leaves the existing code card exactly as it is. It does NOT
 * try to present every JSON document. Three things are deliberately left to the code card:
 *
 *   · anything that does not parse, including a streaming half-block;
 *   · a top-level ARRAY. An array of objects is tabular, and this app already has a renderer for
 *     tabular data (`dataRows` + the artifact card's table). Inventing a second answer for rows
 *     here is the parallel system the brief forbids;
 *   · a document with more than `MAX_FIELDS` top-level keys, or nested past `MAX_DEPTH`. Past
 *     that a labelled record stops being easier to scan than the raw text, and the honest move is
 *     to keep the code view whose `Open` puts the whole thing in the workspace.
 *
 * Everything here is pure so the rules are testable in the repo's node (no-DOM) suite; the
 * component that renders the result is `transcript/JsonView.tsx`.
 */

/** A parsed JSON document, as far as this module cares. */
export type JsonScalar = string | number | boolean | null;
export type JsonValue = JsonScalar | JsonValue[] | { [key: string]: JsonValue };

/** Top-level keys past which a record is no longer easier to scan than its source. */
export const MAX_FIELDS = 14;
/** How many top-level fields are RENDERED before the rest are summarised. */
export const PREVIEW_FIELDS = 10;
/** Nesting past this is summarised rather than laid out. */
export const MAX_DEPTH = 2;
/** A string longer than this is a paragraph, and gets its own line under the label. */
export const LONG_STRING = 48;

/** How one field is presented. Closed set — the renderer has a branch per member and no default. */
export type FieldShape =
  /** `true` / `false`. Rendered as a glyph AND a word; colour is never the only carrier. */
  | { kind: "boolean"; value: boolean }
  /** A number, rendered tabular so a column of them lines up. */
  | { kind: "number"; value: number }
  /** A short string — fits beside its label. */
  | { kind: "text"; value: string }
  /** A long string — wraps on its own line under the label, never scrolls sideways. */
  | { kind: "paragraph"; value: string }
  /** `null`, `[]`, `{}` or `""`. One vocabulary for "nothing here". */
  | { kind: "empty"; reason: "null" | "empty-list" | "empty-object" | "blank" }
  /** An array of scalars — a bulleted list. */
  | { kind: "list"; items: JsonScalar[] }
  /** A nested object, laid out as its own labelled rows. */
  | { kind: "object"; fields: ViewField[] }
  /**
   * Anything this module will not lay out: an array of objects, or a value nested past
   * `MAX_DEPTH`. Carries the compact JSON so the reader still SEES it rather than being told a
   * value exists and shown nothing.
   */
  | { kind: "raw"; json: string; count: number | null };

export interface ViewField {
  /** The JSON key, verbatim — what `Copy` and `Download` still hand back. */
  key: string;
  /** The key as a person reads it. See `humanizeKey`. */
  label: string;
  shape: FieldShape;
}

export interface JsonRecordView {
  fields: ViewField[];
  /** Fields beyond `PREVIEW_FIELDS`, summarised rather than rendered. 0 when all are shown. */
  hiddenCount: number;
}

/** Fence tags whose content is JSON. `json5`/`jsonc` are NOT included: they permit comments and
 *  trailing commas, so `JSON.parse` would reject them and the block would fall through anyway. */
const JSON_LANGUAGES = new Set(["json"]);

export function isJsonLanguage(language: string | null | undefined): boolean {
  return JSON_LANGUAGES.has((language ?? "").trim().toLowerCase());
}

/**
 * Parse, or `null`.
 *
 * Never throws and never repairs. A block still streaming in is a truncated document, and
 * "repairing" it would render a record whose missing half appears as settled fact for as long as
 * the stream takes to finish.
 */
export function parseJson(content: string): JsonValue | null {
  const text = content.trim();
  if (!text) return null;
  // Cheap gate before the parse: the only shapes this module lays out start with `{`.
  if (!text.startsWith("{")) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    return parsed as JsonValue;
  } catch {
    return null;
  }
}

/**
 * `acceptance_met` → `Acceptance met`. `deliverable` → `Deliverable`. `planItemCount` → `Plan item
 * count`.
 *
 * WHY THE KEY IS REWRITTEN AT ALL. The label is the one piece of a result a reader scans, and
 * `acceptance_met` reads as an identifier — it tells them they are looking at a data structure,
 * which is the impression this whole change exists to remove. The raw key is not lost: `Copy` and
 * `Download` still hand back the original document, unchanged.
 *
 * An ALL-CAPS or dotted key (`API_URL`, `run.id`) is left alone — those are names, not phrases,
 * and sentence-casing them would make them wrong.
 */
export function humanizeKey(key: string): string {
  if (!key) return key;
  if (key === key.toUpperCase() && /[A-Z]/.test(key)) return key;
  if (key.includes(".")) return key;
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function isScalar(v: JsonValue): v is JsonScalar {
  return v === null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

/** Compact JSON, capped — what a `raw` shape shows instead of nothing. */
function compact(value: JsonValue, cap = 160): string {
  const text = JSON.stringify(value) ?? "";
  return text.length <= cap ? text : `${text.slice(0, cap - 1)}…`;
}

function shapeOf(value: JsonValue, depth: number): FieldShape {
  if (value === null) return { kind: "empty", reason: "null" };
  if (typeof value === "boolean") return { kind: "boolean", value };
  if (typeof value === "number") return { kind: "number", value };
  if (typeof value === "string") {
    if (!value.trim()) return { kind: "empty", reason: "blank" };
    return value.length > LONG_STRING || value.includes("\n")
      ? { kind: "paragraph", value }
      : { kind: "text", value };
  }
  if (Array.isArray(value)) {
    if (!value.length) return { kind: "empty", reason: "empty-list" };
    // A list of scalars is a list. A list of objects is a TABLE, which this module does not own.
    return value.every(isScalar)
      ? { kind: "list", items: value }
      : { kind: "raw", json: compact(value), count: value.length };
  }
  const entries = Object.entries(value);
  if (!entries.length) return { kind: "empty", reason: "empty-object" };
  if (depth >= MAX_DEPTH) return { kind: "raw", json: compact(value), count: entries.length };
  return { kind: "object", fields: entries.map(([k, v]) => field(k, v, depth + 1)) };
}

function field(key: string, value: JsonValue, depth: number): ViewField {
  return { key, label: humanizeKey(key), shape: shapeOf(value, depth) };
}

/**
 * The record view for a parsed object, or `null` when the code card is the better answer.
 *
 * `null` is not a failure — it is this module declining, and every caller must render the block
 * exactly as it did before when it sees one.
 */
export function recordView(value: JsonValue | null): JsonRecordView | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const entries = Object.entries(value);
  if (!entries.length) return null;
  if (entries.length > MAX_FIELDS) return null;
  const all = entries.map(([k, v]) => field(k, v, 0));
  return {
    fields: all.slice(0, PREVIEW_FIELDS),
    hiddenCount: Math.max(0, all.length - PREVIEW_FIELDS),
  };
}

/**
 * The one entry point a renderer calls: a record view for this fenced block, or `null` to leave it
 * as code.
 */
export function jsonRecordFor(language: string | null | undefined, content: string): JsonRecordView | null {
  if (!isJsonLanguage(language)) return null;
  return recordView(parseJson(content));
}
